import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';
import type { DateTime } from 'luxon';
import type { DragEndEvent } from '@dnd-kit/core';
import { Loading } from '@library/components/Loading';
import { MessageBanner } from '@library/components/banners/MessageBanner';
import { NoResults } from '@library/components/NoResults';
import type { NormalizedProjectionWithPlayer } from '@library/types/streaks';
import { PrimaryButton } from '@library/components/buttons';
import { StreakBoardHeader } from '@bo-components/streak/StreakBoardHeader';
import { StreakBoardRankProjectionRow } from '@bo-components/streak/StreakBoardRankProjectionRow';
import { usePublishStreakProjectionsBoardMutation } from '@bo-services/streakApi';

interface StreakBoardProps {
  selectedDate: DateTime;
  rankedProjections?: NormalizedProjectionWithPlayer[];
  rankedProjectionsLoading: boolean;
  onUnrankProjection: (projection: NormalizedProjectionWithPlayer) => void;
  setRankedProjections: (projections: NormalizedProjectionWithPlayer[]) => void;
}

export const StreakBoard = ({
  selectedDate,
  rankedProjections = [],
  rankedProjectionsLoading,
  onUnrankProjection,
  setRankedProjections
}: StreakBoardProps) => {
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [publishStreakBoardProjections, { isLoading: publishBoardLoading }] =
    usePublishStreakProjectionsBoardMutation();

  // Sensors for detecting pointer actions for dragging
  const sensors = useSensors(
    useSensor(PointerSensor) // Use PointerSensor for mouse/touch dragging
  );

  // Function to handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // If no valid destination or the position hasn't changed, do nothing
    if (!over || active.id === over.id) {
      return;
    }

    // Find the old index and new index of the dragged row
    const oldIndex = rankedProjections.findIndex(
      (item) => item.id === active.id
    );
    const newIndex = rankedProjections.findIndex((item) => item.id === over.id);

    // Reorder the projections based on the drag-and-drop result
    const updatedProjections = arrayMove(rankedProjections, oldIndex, newIndex);

    // Update the projections state
    setRankedProjections(updatedProjections);
  };

  const onPublishBoard = async () => {
    try {
      await publishStreakBoardProjections({
        date: selectedDate.toFormat('yyyy-MM-dd'),
        projection_ids: rankedProjections.map((projection) =>
          Number(projection.id)
        )
      });
      setShowSuccessBanner(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setShowErrorBanner(true);
    }
  };

  useEffect(() => {
    if (showSuccessBanner) {
      setTimeout(() => {
        setShowSuccessBanner(false);
      }, 5000);
    }
  }, [showSuccessBanner]);

  return (
    <div className="bg-soFresh-120 border-soFresh-140 flex h-full flex-[2] shrink-0 flex-col items-start justify-start gap-4 overflow-scroll rounded-2xl rounded-bl-md p-4">
      {rankedProjectionsLoading ? (
        <Loading loadingMessage="Ranked projections loading" />
      ) : (
        <>
          <div className="flex w-full items-center justify-between">
            <h2 className="font-heebo text-soClean-100 flex w-full items-center justify-between gap-4 pt-1 text-base font-semibold leading-4">
              {selectedDate.toFormat('MMMM dd, yyyy')} Board
            </h2>
            <PrimaryButton
              className=""
              handleOnClick={onPublishBoard}
              title="Publish board button"
              disabled={rankedProjectionsLoading || publishBoardLoading}
            >
              Publish
            </PrimaryButton>
          </div>
          {showSuccessBanner && (
            <MessageBanner
              code="SUCCESS"
              message={`Board for ${selectedDate.toFormat('MMMM dd, yyyy')} was successfully published.`}
              onCancel={() => setShowSuccessBanner(false)}
            />
          )}
          {showErrorBanner && (
            <MessageBanner
              code="ERROR"
              message={`Board for ${selectedDate.toFormat('MMMM dd, yyyy')} could not be published.`}
              onCancel={() => setShowErrorBanner(false)}
            />
          )}
          {rankedProjections.length > 0 ? (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={rankedProjections.map((projection) => projection.id)}
                strategy={verticalListSortingStrategy}
              >
                <table className="w-full table-auto space-y-10">
                  <StreakBoardHeader />
                  <tbody>
                    {rankedProjections.map((projection, index) => (
                      <StreakBoardRankProjectionRow
                        key={projection.id}
                        projection={projection}
                        onUnrankProjectionWrapped={() =>
                          onUnrankProjection(projection)
                        }
                        temporaryRank={index + 1}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          ) : (
            <NoResults label="No ranked projections were found on this date" />
          )}
        </>
      )}
    </div>
  );
};
